import React, { useEffect, useState } from 'react';
import {
    Modal,
    Form,
    Input,
    Select,
    Button,
    Row,
    Col,
    Space,
    DatePicker,
    message
} from 'antd';
import { adminAPI } from '../../utils/api';
import dayjs from 'dayjs';

const { Option } = Select;

const ConsumerEditModal = ({ visible, onCancel, onSuccess, consumer }) => {
    const [coaches, setCoaches] = useState([]);
    const [newCoachName, setNewCoachName] = useState('');
    const [addingCoach, setAddingCoach] = useState(false);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchCoaches();
    }, []);

    const fetchCoaches = async () => {
        try {
            const response = await adminAPI.getCoaches();
            setCoaches(response.data.coaches || []);
        } catch (error) {
            console.error('Failed to fetch coaches');
        }
    };

    const handleAddCoach = async (e) => {
        e.preventDefault(); // Prevent form submission if button inside form
        if (!newCoachName.trim()) return;

        try {
            setAddingCoach(true);
            const response = await adminAPI.createCoach({ name: newCoachName });
            message.success('Coach added successfully');
            setNewCoachName('');
            await fetchCoaches(); // Refresh list

            // Auto-select the new coach
            /* The backend returns the new coach object. We can match by name or just re-fetch.
               Since existing logic uses values.coach_name (string), 
               we just need the name available in the options. 
            */
            form.setFieldsValue({ coach_name: response.data.coach.name });

        } catch (error) {
            message.error(error.response?.data?.error || 'Failed to add coach');
        } finally {
            setAddingCoach(false);
        }
    };

    useEffect(() => {
        if (visible) {
            if (consumer) {
                form.setFieldsValue({
                    ...consumer,
                    date_of_birth: consumer.date_of_birth ? dayjs(consumer.date_of_birth) : null,
                    anniversary_date: consumer.anniversary_date ? dayjs(consumer.anniversary_date) : null,
                    start_date: consumer.start_date ? dayjs(consumer.start_date) : dayjs(),
                });
            } else {
                form.resetFields();
                form.setFieldsValue({
                    start_date: dayjs(),
                    gender: 'male',
                    is_active: true
                });
            }
        } else {
            form.resetFields();
        }
    }, [visible, consumer, form]);

    const handleSubmit = async (values) => {
        try {
            setLoading(true);
            const consumerData = {
                ...values,
                date_of_birth: values.date_of_birth ? values.date_of_birth.format('YYYY-MM-DD') : undefined,
                anniversary_date: values.anniversary_date ? values.anniversary_date.format('YYYY-MM-DD') : undefined,
                start_date: values.start_date ? values.start_date.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
                is_active: values.is_active !== undefined ? values.is_active : true
            };

            if (consumer) {
                await adminAPI.updateConsumer(consumer.id, consumerData);
                message.success('Consumer updated successfully');
            } else {
                await adminAPI.createConsumer(consumerData);
                message.success('Consumer added successfully');
            }

            onSuccess();
        } catch (error) {
            console.error(error);
            message.error(consumer ? 'Failed to update consumer' : 'Failed to add consumer');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={consumer ? "Edit Consumer" : "Add New Consumer"}
            open={visible}
            onCancel={onCancel}
            footer={null}
            width={800}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
            >
                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="name"
                            label="Full Name"
                            rules={[{ required: true, message: 'Please enter consumer name' }]}
                        >
                            <Input placeholder="Enter full name" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="email"
                            label="Email"
                            rules={[
                                { type: 'email', message: 'Please enter valid email' }
                            ]}
                        >
                            <Input placeholder="Enter email (optional)" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="phone"
                            label="Phone Number"
                        >
                            <Input placeholder="Enter phone number (optional)" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="gender"
                            label="Gender"
                            rules={[{ required: true, message: 'Please select gender' }]}
                        >
                            <Select placeholder="Select gender">
                                <Option value="male">Male</Option>
                                <Option value="female">Female</Option>
                                <Option value="other">Other</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="plan_type"
                            label="Plan Type"
                            rules={[{ required: true, message: 'Please select plan type' }]}
                        >
                            <Select placeholder="Select plan type">
                                <Option value="sample">Sample User</Option>
                                <Option value="3_day">3 Day Plan</Option>
                                <Option value="membership">Membership</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="coach_name"
                            label="Coach Name"
                        >
                            <Select
                                placeholder="Select or add coach"
                                dropdownRender={(menu) => (
                                    <>
                                        {menu}
                                        <div style={{ padding: '8px', borderTop: '1px solid #e8e8e8' }}>
                                            <Space>
                                                <Input
                                                    placeholder="New Coach Name"
                                                    value={newCoachName}
                                                    onChange={e => setNewCoachName(e.target.value)}
                                                    onKeyDown={e => e.stopPropagation()} // Prevent closing dropdown on type
                                                    style={{ width: 180 }}
                                                />
                                                <Button type="primary" onClick={handleAddCoach} loading={addingCoach} size="small">
                                                    + Add
                                                </Button>
                                            </Space>
                                        </div>
                                    </>
                                )}
                            >
                                {coaches.map(coach => (
                                    <Option key={coach.id} value={coach.name}>{coach.name}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="date_of_birth"
                            label="Date of Birth"
                        >
                            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder="Date of birth" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="anniversary_date"
                            label="Anniversary Date"
                        >
                            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder="Anniversary date" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="start_date"
                            label="Start Date"
                            rules={[{ required: true, message: 'Please select start date' }]}
                        >
                            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item
                            name="address"
                            label="Address"
                        >
                            <Input.TextArea rows={2} placeholder="Optional address" />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item
                            name="notes"
                            label="Notes"
                        >
                            <Input.TextArea rows={3} placeholder="Enter any additional notes about this consumer" />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                    <Space>
                        <Button onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            {consumer ? "Update Consumer" : "Add Consumer"}
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ConsumerEditModal;
